/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { trans } from '@spotahome/soyuz/client';

import { DSIcon } from '@spotahome/ui-library/base';
import { ModalHeader, Chip, BaseChip } from '@spotahome/ui-library';
import { ListingFormatter } from '@spotahome/ui-library/src/tenant/utils/images';

import MainSliderLabels from '../Slider/MainSliderLabels';

import LazyPhoto from './LazyPhoto';

import './FullScreenPhotoList.scss';
import FullScreenPhotoListNavigator from './FullScreenPhotoListNavigator';

const VIEWPORT_TRIGGER_OFFSET_PX = 80;

const groupPhotosByCaption = photos => {
  const sectionsMap = new Map();
  photos.forEach(photo => {
    const caption = photo.caption ?? trans('listing.fullScreenPhotoList.other');
    if (!sectionsMap.has(caption)) {
      sectionsMap.set(caption, { caption, photos: [] });
    }
    sectionsMap.get(caption).photos.push({
      id: photo.id,
      caption,
      verified: photo.verified,
      src: ListingFormatter.buildPhotoUrl(photo.id, photo.verified),
      squareSrc: ListingFormatter.buildSquarePhotoUrl(photo.id)
    });
  });
  return Array.from(sectionsMap.values());
};

const FullScreenPhotoListContent = ({
  onClose,
  onImageClick,
  headerActions = [],
  photos,
  listingId
}) => {
  const sections = useMemo(() => groupPhotosByCaption(photos), [photos]);
  const [activeSection, setActiveSection] = useState(
    sections[0]?.caption ?? null
  );

  const contentRef = useRef(null);
  const sectionRefsRef = useRef([]);

  const scrollToSection = caption => {
    const el = document.querySelector(`[data-caption="${caption}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    const content = contentRef.current;
    if (!content || sections.length === 0) {
      return undefined;
    }

    const handleScroll = () => {
      const contentRect = content.getBoundingClientRect();
      const triggerY = contentRect.top + VIEWPORT_TRIGGER_OFFSET_PX;
      sections.forEach((section, i) => {
        const el = sectionRefsRef.current[i];
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.top <= triggerY && triggerY < rect.bottom) {
          setActiveSection(section.caption);
        }
      });
    };

    content.addEventListener('scroll', handleScroll, { passive: true });
    // Run after layout so section refs are populated; also support scroll on modal root if it's the actual scroll container

    const modalRoot =
      content.closest('[role="dialog"]') || content.parentElement;
    if (modalRoot && modalRoot !== content) {
      modalRoot.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      content.removeEventListener('scroll', handleScroll);
      if (modalRoot && modalRoot !== content) {
        modalRoot.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  return (
    <Fragment>
      <div className="full-screen-photo-list__top-bar">
        <ModalHeader
          backButtonClassName="full-screen-photo-list__back-button"
          label={trans('listing.fullScreenPhotoList.back')}
          onRequestClose={onClose}
          showBack
          showClose={false}
          className="full-screen-photo-list__header"
          titleClassName="full-screen-photo-list__back-title"
        />
        {headerActions && headerActions.length > 0 && (
          <div className="full-screen-photo-list__header-actions">
            {headerActions.map((action, i) => (
              <Chip
                key={action.label ?? i}
                label={action.label}
                onClick={action.onClick}
                colorSchema={BaseChip.SCHEMAS.light}
                withBorder
                renderLeadingIcon={
                  action.iconName
                    ? () => <DSIcon name={action.iconName} size="sm" />
                    : null
                }
              />
            ))}
          </div>
        )}
      </div>

      <MainSliderLabels
        onBackClick={onClose}
        listingId={listingId}
        className="full-screen-photo-list__top-bar--mobile"
      />

      <FullScreenPhotoListNavigator
        sections={sections}
        scrollToSection={scrollToSection}
        activeSection={sections.findIndex(
          section => section.caption === activeSection
        )}
      />

      {headerActions.length > 0 && (
        <div className="full-screen-photo-list__actions-wrapper">
          {headerActions.map((action, i) => (
            <Chip
              key={action.label ?? i}
              label={action.label}
              onClick={action.onClick}
              colorSchema={BaseChip.SCHEMAS.light}
              withBorder
              renderLeadingIcon={
                action.iconName
                  ? () => <DSIcon name={action.iconName} size="sm" />
                  : null
              }
            />
          ))}
        </div>
      )}

      <div className="full-screen-photo-list__body">
        <nav className="full-screen-photo-list__sidebar">
          <ul className="full-screen-photo-list__nav-list">
            {sections.map(({ caption }) => (
              <li key={caption} className="full-screen-photo-list__nav-item">
                <button
                  type="button"
                  className={classNames('full-screen-photo-list__nav-button', {
                    'full-screen-photo-list__nav-button--active':
                      activeSection === caption
                  })}
                  onClick={() => scrollToSection(caption)}
                >
                  {caption}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div ref={contentRef} className="full-screen-photo-list__content">
          {sections.map(({ photos: sectionPhotos }, index) =>
            sectionPhotos.map((photo, photoIndex) => (
              <LazyPhoto
                key={photo.id}
                src={photo.src}
                caption={photo.caption}
                onImageClick={() => onImageClick(photo.id)}
                className={classNames('full-screen-photo-list__image-wrapper', {
                  'full-screen-photo-list__image-wrapper--first':
                    photoIndex === 0,
                  'full-screen-photo-list__image-wrapper--full-width':
                    photoIndex % 3 === 0
                })}
                imageClassName="full-screen-photo-list__image"
                {...(photoIndex === 0
                  ? {
                      ref: el => (sectionRefsRef.current[index] = el),
                      dataAttributes: {
                        'data-caption': photo.caption
                      }
                    }
                  : {})}
              />
            ))
          )}
        </div>
      </div>
    </Fragment>
  );
};

FullScreenPhotoListContent.propTypes = {
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      caption: PropTypes.string.isRequired,
      verified: PropTypes.bool
    })
  ).isRequired,
  headerActions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired
    })
  ),
  onClose: PropTypes.func.isRequired,
  onImageClick: PropTypes.func.isRequired,
  listingId: PropTypes.string.isRequired
};

export default FullScreenPhotoListContent;
